import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { security } from '../../utils/security';
import ProjectsAdmin from './components/ProjectsAdmin';
import SkillsAdmin from './components/SkillsAdmin';
import ExperienceAdmin from './components/ExperienceAdmin';
import CertificationsAdmin from './components/CertificationsAdmin';
import AboutAdmin from './components/AboutAdmin';
import PersistenceControls from '../../components/admin/PersistenceControls';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('data');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [error, setError] = useState('');
  const [sessionWarning, setSessionWarning] = useState(false);
  const [rateLimitError, setRateLimitError] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState(null);

  const adminTabs = [
    { id: 'data', label: 'Data Management', icon: 'Database' },
    { id: 'projects', label: 'Projects', icon: 'Briefcase' },
    { id: 'skills', label: 'Skills', icon: 'Code' },
    { id: 'experience', label: 'Experience', icon: 'User' },
    { id: 'certifications', label: 'Certifications', icon: 'Award' },
    { id: 'about', label: 'About', icon: 'Info' }
  ];

  // Check authentication and security status on mount
  useEffect(() => {
    security.checkSecurityHeaders();
    
    // Check if already authenticated
    if (security.validateSession()) {
      setIsAuthenticated(true);
    }
    
    // Check if account is locked
    if (security.isAccountLocked()) {
      setIsLocked(true);
      setLockoutTime(security.getLockoutTimeRemaining());
    }
    
    setRemainingAttempts(security.getRemainingAttempts());
  }, []);

  // Session timeout warning
  useEffect(() => {
    if (isAuthenticated) {
      const warningTimer = setTimeout(() => {
        setSessionWarning(true);
        setTimeout(() => setSessionWarning(false), 5000);
             }, 10 * 60 * 1000); // 10 minutes (5 minutes before 15-minute timeout)
      
      return () => clearTimeout(warningTimer);
    }
  }, [isAuthenticated]);

  // Auto-logout on inactivity
  useEffect(() => {
    if (isAuthenticated) {
      const activityHandler = () => {
        security.extendSession();
      };
      
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, activityHandler, true);
      });
      
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, activityHandler, true);
        });
      };
    }
  }, [isAuthenticated]);

  // Update lockout countdown
  useEffect(() => {
    if (isLocked && lockoutTime > 0) {
      const timer = setInterval(() => {
        const remaining = security.getLockoutTimeRemaining();
        setLockoutTime(remaining);
        
        if (remaining === 0) {
          setIsLocked(false);
          setRemainingAttempts(3);
          setError('');
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutTime]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setRateLimitError('');
    
    // Check rate limiting
    const rateLimit = security.checkRateLimit();
    if (!rateLimit.allowed) {
      setRateLimitError(`Too many attempts. Try again in ${Math.ceil(rateLimit.waitTime / 60)} minutes.`);
      return;
    }
    
    // Check if account is locked
    if (security.isAccountLocked()) {
      setIsLocked(true);
      setLockoutTime(security.getLockoutTimeRemaining());
      setError(`Account locked. Try again in ${lockoutTime} minutes.`);
      return;
    }
    
    try {
      // Validate password with SHA-256 hashing
      const isValidPassword = await security.validatePassword(password);
      
      if (isValidPassword) {
        // Check if 2FA is enabled
        if (security.isTwoFactorEnabled()) {
          setTwoFactorRequired(true);
          setPassword(''); // Clear password from memory
          return;
        }
        
        // Successful login without 2FA
        security.clearFailedAttempts();
        security.createSession();
        setIsAuthenticated(true);
        setPassword('');
        setError('');
      } else {
        // Failed login
        const attempts = security.recordFailedAttempt();
        const remaining = security.getRemainingAttempts();
        
        if (remaining === 0) {
          setIsLocked(true);
          setLockoutTime(security.getLockoutTimeRemaining());
          setError(`Too many failed attempts. Account locked for ${lockoutTime} minutes.`);
        } else {
          setError(`Invalid credentials. ${remaining} attempts remaining.`);
        }
        
        setRemainingAttempts(remaining);
        setPassword('');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
      setPassword('');
    }
  };

  const handleTwoFactorLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    const secret = security.getTOTPSecret();
    let isValid = false;
    
    try {
      if (useBackupCode) {
        isValid = security.validateBackupCode(backupCode);
      } else {
        isValid = await security.validateTOTP(totpToken, secret);
      }
      
      if (isValid) {
        // Successful 2FA
        security.clearFailedAttempts();
        security.createSession();
        setIsAuthenticated(true);
        setTwoFactorRequired(false);
        setTotpToken('');
        setBackupCode('');
        setError('');
      } else {
        // Failed 2FA
        const attempts = security.recordFailedAttempt();
        const remaining = security.getRemainingAttempts();
        
        if (remaining === 0) {
          setIsLocked(true);
          setLockoutTime(security.getLockoutTimeRemaining());
          setTwoFactorRequired(false);
          setError(`Too many failed attempts. Account locked for ${lockoutTime} minutes.`);
        } else {
          setError(`Invalid ${useBackupCode ? 'backup code' : 'authenticator code'}. ${remaining} attempts remaining.`);
        }
        
        setTotpToken('');
        setBackupCode('');
        setRemainingAttempts(remaining);
      }
    } catch (error) {
      console.error('2FA validation error:', error);
      setError('Authentication failed. Please try again.');
      setTotpToken('');
      setBackupCode('');
    }
  };

  const handleSetupTwoFactor = () => {
    const setupData = security.enableTwoFactor();
    setTwoFactorSetupData(setupData);
    setShowTwoFactorSetup(true);
  };

  const handleDisableTwoFactor = () => {
    if (window.confirm('Are you sure you want to disable two-factor authentication?')) {
      security.disableTwoFactor();
      setShowTwoFactorSetup(false);
      setTwoFactorSetupData(null);
    }
  };

  const handleLogout = () => {
    security.clearSession();
    setIsAuthenticated(false);
    setPassword('');
    setSessionWarning(false);
    setTwoFactorRequired(false);
    setTotpToken('');
    setBackupCode('');
    setShowTwoFactorSetup(false);
    setTwoFactorSetupData(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Admin Login | Musa Yusuf Magoban</title>
        </Helmet>
        
        <Header />
        
        <main className="pt-16 flex items-center justify-center min-h-screen">
          <div className="bg-surface rounded-lg p-8 security-shadow max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <Icon name="Lock" size={48} className="text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Admin Access</h1>
              <p className="text-text-secondary">Secure administrative interface</p>
              
              {isLocked && (
                <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <Icon name="AlertTriangle" size={20} className="text-destructive mx-auto mb-2" />
                  <p className="text-sm text-destructive font-medium">
                    Account locked for {lockoutTime} minutes
                  </p>
                </div>
              )}
            </div>
            
            {twoFactorRequired ? (
              <form onSubmit={handleTwoFactorLogin} className="space-y-4">
                <div className="text-center mb-4">
                  <Icon name="Shield" size={24} className="text-secondary mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
                </div>
                
                {!useBackupCode ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Authenticator Code
                    </label>
                    <input
                      type="text"
                      value={totpToken}
                      onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-center tracking-widest"
                      placeholder="000000"
                      maxLength="6"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Backup Code
                    </label>
                    <input
                      type="text"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter backup code"
                      required
                    />
                  </div>
                )}
                
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}
                
                <button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors font-medium"
                >
                  Verify {useBackupCode ? 'Backup Code' : 'Code'}
                </button>
                
                <button
                  type="button"
                  onClick={() => setUseBackupCode(!useBackupCode)}
                  className="w-full text-sm text-text-secondary hover:text-foreground transition-colors"
                >
                  {useBackupCode ? 'Use authenticator app' : 'Use backup code instead'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter admin password"
                    disabled={isLocked}
                    required
                  />
                </div>
                
                {rateLimitError && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <Icon name="Clock" size={16} className="text-destructive inline mr-2" />
                    <span className="text-sm text-destructive">{rateLimitError}</span>
                  </div>
                )}
                
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}
                
                {!isLocked && remainingAttempts < 3 && (
                  <div className="p-3 bg-secondary/10 border border-secondary/20 rounded-md">
                    <p className="text-sm text-secondary">
                      {remainingAttempts} attempts remaining
                    </p>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isLocked || !!rateLimitError}
                  className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLocked ? 'Account Locked' : 'Secure Login'}
                </button>
              </form>
            )}
            

          </div>
        </main>
      </div>
    );
  }

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'data':
        return <PersistenceControls />;
      case 'projects':
        return <ProjectsAdmin />;
      case 'skills':
        return <SkillsAdmin />;
      case 'experience':
        return <ExperienceAdmin />;
      case 'certifications':
        return <CertificationsAdmin />;
      case 'about':
        return <AboutAdmin />;
      default:
        return <PersistenceControls />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin Panel | Musa Yusuf Magoban</title>
      </Helmet>
      
      <Header />
      
      <main className="pt-16">
        {/* Header Section */}
        <section className="py-8 bg-gradient-to-br from-primary/5 to-secondary/5 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground flex items-center">
                  <Icon name="Settings" size={32} className="text-primary mr-3" />
                  Admin Panel
                </h1>
                <p className="text-text-secondary mt-1">
                  Manage your website content and settings
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                {security.isTwoFactorEnabled() ? (
                  <button
                    onClick={handleDisableTwoFactor}
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors font-medium flex items-center"
                  >
                    <Icon name="ShieldOff" size={16} className="mr-2" />
                    Disable 2FA
                  </button>
                ) : (
                  <button
                    onClick={handleSetupTwoFactor}
                    className="bg-accent text-accent-foreground px-4 py-2 rounded-md hover:bg-accent/90 transition-colors font-medium flex items-center"
                  >
                    <Icon name="Shield" size={16} className="mr-2" />
                    Enable 2FA
                  </button>
                )}
                
                <button
                  onClick={handleLogout}
                  className="bg-destructive text-destructive-foreground px-4 py-2 rounded-md hover:bg-destructive/90 transition-colors font-medium flex items-center"
                >
                  <Icon name="LogOut" size={16} className="mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8 overflow-x-auto">
              {adminTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </section>

        {/* Session Warning */}
        {sessionWarning && (
          <div className="fixed top-20 right-4 bg-secondary/90 backdrop-blur-sm border border-secondary/20 rounded-lg p-4 shadow-lg z-50 max-w-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-secondary" />
              <p className="text-sm text-secondary font-medium">
                Session expires in 5 minutes
              </p>
            </div>
          </div>
        )}

        {/* Content Area */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {renderActiveTab()}
          </div>
        </section>

        {/* 2FA Setup Modal */}
        {showTwoFactorSetup && twoFactorSetupData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-surface rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-foreground">Setup Two-Factor Authentication</h3>
                <button
                  onClick={() => setShowTwoFactorSetup(false)}
                  className="p-2 text-text-secondary hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-text-secondary mb-4">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  
                  <div className="bg-white p-4 rounded-lg inline-block">
                    <img 
                      src={twoFactorSetupData.qrCodeUrl} 
                      alt="2FA QR Code" 
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Secret Key (Manual Entry)
                  </label>
                  <div className="bg-muted p-3 rounded-md">
                    <code className="text-sm font-mono break-all">{twoFactorSetupData.secret}</code>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Backup Code (Save This!)
                  </label>
                  <div className="bg-accent/10 border border-accent/20 p-3 rounded-md">
                    <code className="text-sm font-mono text-accent font-semibold">{twoFactorSetupData.backupCode}</code>
                    <p className="text-xs text-text-secondary mt-2">
                      Save this backup code in a secure location. You can use it to access your account if you lose your authenticator device.
                    </p>
                  </div>
                </div>

                <div className="bg-secondary/10 border border-secondary/20 p-3 rounded-md">
                  <div className="flex items-start">
                    <Icon name="AlertCircle" size={16} className="text-secondary mr-2 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-secondary">
                      <p className="font-medium mb-1">Important:</p>
                      <ul className="text-xs space-y-1">
                        <li>• Save your backup code before closing this dialog</li>
                        <li>• Test your authenticator app before logging out</li>
                        <li>• Keep your authenticator device secure</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowTwoFactorSetup(false)}
                  className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors font-medium"
                >
                  I've Saved My Backup Code
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin; 